import React, { useState, useEffect } from 'react';
import { Table, DatePicker, Input, message, Select } from 'antd';
import { FaClockRotateLeft } from "react-icons/fa6";
import { TbRotateClockwise2 } from "react-icons/tb";
import { PiClockCountdown } from "react-icons/pi";
import { GrHome } from "react-icons/gr";
import { TbHomeSignal } from "react-icons/tb";
import { IoTodayOutline } from "react-icons/io5";
import { CgSandClock } from "react-icons/cg";
import { TbClockBolt } from "react-icons/tb";
import { SearchOutlined } from '@ant-design/icons';
import { getRequest } from '../../../../service';
import { getEmployeeId } from '../../../../utils/localStorage';
import ModalWrapper from "../../../../Modal";
import AttendanceDetails from "../../../../modals/viewAttendance";
import { MdOutlineAddHomeWork } from "react-icons/md";
import { MdOutlineAddHome } from "react-icons/md";
import { getUserIanaTimeZone, nowInUserTz, parseToUserTz, toUtcDayBounds } from '../../../../utils/dateTime';
import { dayjs } from '../../../../utils/dateTime';

const Attendance = () => {
    const userTimeZone = getUserIanaTimeZone();

    const [selectedMonth, setSelectedMonth] = useState(() => {
        return nowInUserTz().startOf('month');
    });
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [employees, setEmployees] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAttendanceData, setSelectedAttendanceData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [shifts, setShifts] = useState([]);
    const [selectedShiftId, setSelectedShiftId] = useState(null);
    const [shiftConfig, setShiftConfig] = useState(null);

    const formatDateInUserTimezone = (date, format = 'YYYY-MM-DD') => {
        if (!date) return null;
        return parseToUserTz(date)?.format(format) ?? null;
    };

    // Helper function to get date range in user's timezone
    const getDateRangeInUserTimezone = (monthDate) => {
        const start = monthDate.tz(userTimeZone).startOf('month');
        const end = monthDate.tz(userTimeZone).endOf('month');
        return {
            start: start.format('YYYY-MM-DD'),
            end: end.format('YYYY-MM-DD')
        };
    };

    // Fetch employee data from /api/companies/reportto with optional date range
    const fetchEmployees = async (startDate, endDate) => {
        try {
            let url = '/api/companies/reportto';
            if (startDate && endDate) {
                url += `?startDate=${startDate}&endDate=${endDate}`;
            }
            const response = await getRequest(url);
            if (response && response.employees && Array.isArray(response.employees)) {
                const employeeMap = {};
                
                response.employees.forEach(employee => {
                    if (employee._id) {
                        employeeMap[employee._id] = {
                            id: employee._id,
                            name: employee.name || 'Unknown Employee',
                            department: employee.department || 'Department',
                            designation: employee.designation || 'Designation'
                        };
                    }
                });
                
                setEmployees(employeeMap);
            }
        } catch (error) {
            console.error('Error fetching employee data:', error);
        }
    };

    // Fetch shifts list and set default selected shift
    const fetchShifts = async () => {
        try {
            const res = await getRequest('/api/companies/shifts');
            if (res && Array.isArray(res.shifts)) {
                setShifts(res.shifts);
                // Default: first shift
                if (!selectedShiftId && res.shifts.length > 0) {
                    const firstShift = res.shifts[0];
                    setSelectedShiftId(firstShift._id);
                    setShiftConfig(firstShift);
                }
            } else {
                setShifts([]);
            }
        } catch (error) {
            console.error('Error fetching shifts:', error);
            setShifts([]);
        }
    };

    const getStatusIcon = (status) => {
        const statusLower = status?.toLowerCase();
        switch(statusLower) {
            case 'present':
                return { icon: '✓', color: 'green' };
            case 'absent': 
                return { icon: '✗', color: 'red' };
            case 'late':
                return { icon: <FaClockRotateLeft className='text-red-500' />, color: '' };
            case 'late & early':
                return { icon: <TbRotateClockwise2 className='text-orange-500' />, color: '' };
            case 'early':
                return { icon: <TbClockBolt className='text-blue-500' />, color: '' };
            case 'missing':
                return { icon: <CgSandClock className='text-purple-500' />, color: '' };
            case 'holiday':
                return { icon: <MdOutlineAddHomeWork className='text-green-800' />, color: '' };
            case 'leave':
                return { icon: <GrHome className='text-green-500' />, color: '' };
            case 'earlyout':
            case 'early out':
                return { icon: <PiClockCountdown className='text-green-500'/>, color: '' };
            case 'half':
            case 'halfday':
                return { icon: <MdOutlineAddHome className='text-yellow-500' />, color: '' };
            case 'wfh':
                return { icon: <TbHomeSignal className='text-blue-500'/>, color: '' };
            default:
                return { icon: '-', color: '#ccc' };
        }
    };

    // Helper to convert working hour values (number or "HH:mm") to decimal hours
    const parseWorkingHours = (value) => {
        if (!value) return 0;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const parts = value.split(':');
            if (parts.length === 2) {
                const hours = parseFloat(parts[0]) || 0;
                const minutes = parseFloat(parts[1]) || 0;
                return hours + minutes / 60;
            }
            const num = parseFloat(value);
            return isNaN(num) ? 0 : num;
        }
        return 0;
    };

    const fetchAttendanceData = async (startDate, endDate) => {
        const companyId = getEmployeeId();
        if (!companyId) {
            message.error('Company ID not found');
            return;
        }

        setLoading(true);
        try {
            // Send dates in UTC format to server
            const { start: startUTC } = toUtcDayBounds(startDate);
            const { end: endUTC } = toUtcDayBounds(endDate);
            
            const response = await getRequest(`/api/myattendance/byCompany?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`);
            
            const currentEmployees = Object.values(employees);
            const employeeList = currentEmployees.map(employee => {
                const employeeId = employee.id || employee._id;
                const dates = {};
                
                if (response.attendanceLogs) {
                    response.attendanceLogs.forEach(log => {
                        if (log.employeeId === employeeId) {
                            // Convert date to user's timezone
                            const date = formatDateInUserTimezone(log.date);
                            
                            let workingHour = log.workingHour;
                            let overtime = log.overtime;

                            if (shiftConfig && shiftConfig.workingHours && workingHour) {
                                const workedHours = parseWorkingHours(workingHour);
                                const requiredHours = parseWorkingHours(shiftConfig.workingHours);
                                if (workedHours > requiredHours) {
                                    const extra = workedHours - requiredHours;
                                    overtime = extra.toFixed(2);
                                }
                            }

                            dates[date] = {
                                _id: log._id,
                                status: log.status,
                                checkIn: log.checkIn,
                                checkOut: log.checkOut,
                                workingHour,
                                overtime
                            };
                        }
                    });
                }
                
                return {
                    id: employeeId,
                    employeeName: employee.name || employee.employeeName || 'Unknown Employee',
                    designation: employee.designation || 'Designation',
                    department: employee.department || 'Department',
                    dates: dates
                };
            });
            
            setAttendanceData(employeeList);
        } catch (error) {
            console.error('Error fetching attendance data:', error);
            message.error('Failed to fetch attendance data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShifts();
    }, []);

    useEffect(() => {
        const loadData = async () => {
            const { start, end } = getDateRangeInUserTimezone(selectedMonth);
            await fetchEmployees(start, end);
        };
        loadData();
    }, [selectedMonth]);

    useEffect(() => {
        if (Object.keys(employees).length > 0 && shiftConfig) {
            const { start, end } = getDateRangeInUserTimezone(selectedMonth);
            fetchAttendanceData(start, end);
        }
    }, [employees, selectedMonth, shiftConfig]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText]);

    const getDayColumns = () => {
        const columns = [];
        const daysInMonth = selectedMonth.daysInMonth();
        const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

        const workingDaysSet = new Set(
            Array.isArray(shiftConfig?.workingDays) ? shiftConfig.workingDays : []
        );

        for (let i = 1; i <= daysInMonth; i++) {
            // Create date in user's timezone
            const date = nowInUserTz().month(selectedMonth.month()).year(selectedMonth.year()).date(i);
            const dayOfWeek = weekDays[date.day()];
            const formattedDate = date.format('YYYY-MM-DD');
            
            columns.push({
                title: (
                    <div>
                        <div>{dayOfWeek}</div>
                        <div>{i}</div>
                    </div>
                ),
                dataIndex: ['dates', formattedDate],
                key: `day${i}`,
                width: 50,
                align: 'center',
                render: (dateData, record, index) => {
                    const currentDate = nowInUserTz().month(selectedMonth.month()).year(selectedMonth.year()).date(i);
                    const fullDayName = currentDate.format('dddd');
                    const today = nowInUserTz();

                    const isWorkingDay = workingDaysSet.size === 0 || workingDaysSet.has(fullDayName);
                    const isOffDay = !isWorkingDay;
                    
                    if (isOffDay) {
                        return <span className="attendance-icon weekend">-</span>;
                    }
                    
                    if (currentDate.isAfter(today, 'day')) {
                        return <span className="attendance-icon upcoming">-</span>;
                    }
                    
                    if (!dateData) {
                        return (
                            <span 
                                className="attendance-icon absent"
                                onClick={() => handleCellClick(dateData, record, formattedDate)}
                                title="Absent"
                            >
                                ✗
                            </span>
                        );
                    }
                    
                    const status = dateData.status || 'empty';
                    const { icon, color } = getStatusIcon(status);
                    return (
                        <span 
                            className={`attendance-icon ${status.toLowerCase()}`} 
                            style={{color}}
                            onClick={() => handleCellClick(dateData, record, formattedDate)}
                        >
                            {icon}
                        </span>
                    );
                }
            });
        }
        return columns;
    };

    const columns = [
        {
            title: 'Employee Name',
            dataIndex: 'employeeName',
            key: 'employeeName',
            fixed: 'left',
            width: 150,
            filteredValue: searchText ? [searchText] : null,
            onFilter: (value, record) => 
                record.employeeName.toLowerCase().includes(value.toLowerCase())
        },
        {
            title: 'Designation',
            dataIndex: 'designation',
            key: 'designation',
            fixed: 'left',
            width: 120
        },
        ...getDayColumns()
    ];

    const handleMonthChange = (date) => {
        if (date) {
            setSelectedMonth(date.tz(userTimeZone).startOf('month'));
        } else {
            setSelectedMonth(nowInUserTz().startOf('month'));
        }
    };

    const handleShiftChange = (value) => {
        setSelectedShiftId(value);
        const shift = shifts.find(s => s._id === value);
        if (shift) {
            setShiftConfig(shift);
        } else {
            setShiftConfig(null);
        }
    };

    const handleSearch = (e) => {
        setSearchText(e.target.value);
    };

    const handleCellClick = (dateData, record, date) => {
        const attendanceInfo = dateData ? {
            employeeName: record.employeeName,
            designation: record.designation,
            date: date,
            reqHour: record.reqHour || 9,
            employeeId: record.id,
            _id: dateData._id || dateData.id,
            ...dateData
        } : {
            employeeName: record.employeeName,
            designation: record.designation,
            date: date,
            reqHour: record.reqHour || 9,
            employeeId: record.id,
            status: 'absent',
            checkIn: '',
            checkOut: '',
            workingHour: '',
            overtime: '',
            isAbsent: true
        };
        
        setSelectedAttendanceData(attendanceInfo);
        setIsModalOpen(true);
    };

    const filteredData = attendanceData.filter(employee => 
        !searchText || 
        employee.employeeName.toLowerCase().includes(searchText.toLowerCase()) ||
        employee.designation.toLowerCase().includes(searchText.toLowerCase())
    );

    const handleModalClose = () => {
        setIsModalOpen(false);
        const { start, end } = getDateRangeInUserTimezone(selectedMonth);
        fetchAttendanceData(start, end);
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const handleShowSizeChange = (current, size) => {
        setCurrentPage(1);
        setPageSize(size);
    };

    return (
        <>
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <div className="flex gap-4 items-center">
                    <Input 
                        placeholder="Search employee" 
                        style={{ width: 250 }} 
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={handleSearch}
                    />
                    <Select
                        style={{ minWidth: 260 }}
                        placeholder="Select shift"
                        value={selectedShiftId || undefined}
                        onChange={handleShiftChange}
                        options={shifts.map(shift => ({
                            value: shift._id,
                            label: shift.shiftName
                        }))}
                    />
                </div>
                <DatePicker.MonthPicker 
                    placeholder="Select month" 
                    value={selectedMonth}
                    onChange={handleMonthChange}
                />
            </div>
            <div className="flex justify-start my-5">
                <span className="text-sm flex items-center gap-4">
                    <span className="mr-2 flex items-center gap-1"> <span className='text-green-500'>✓</span> Present</span>
                    <span className="mr-2 flex items-center gap-1"> <span className='text-red-500'>✗</span> Absent</span>
                    <span className="mr-2 flex items-center gap-1"><FaClockRotateLeft className='text-red-400' /> Late</span>
                    <span className="mr-2 flex items-center gap-1"><TbClockBolt className='text-blue-500' /> Early</span>
                    <span className="mr-2 flex items-center gap-1"><TbRotateClockwise2 className='text-orange-500' /> Late & Early</span>
                    <span className="mr-2 flex items-center gap-1"><CgSandClock className='text-purple-500' /> Missing</span>
                    <span className="mr-2 flex items-center gap-1"><GrHome className='text-green-500' /> Leave</span>
                    <span className="mr-2 flex items-center gap-1"><MdOutlineAddHomeWork className='text-green-800' /> Holiday</span>
                    <span className="mr-2 flex items-center gap-1"><MdOutlineAddHome className='text-yellow-500' /> Half Day</span>
                </span>
            </div>
            
            <Table 
                columns={columns} 
                dataSource={filteredData}
                rowKey="id"
                scroll={{ x: 1500 }}
                loading={loading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    onChange: handlePageChange,
                    onShowSizeChange: handleShowSizeChange,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
                bordered
                size="small"
                className="attendance-table"
            />

            <style jsx>{`
                .attendance-icon {
                    display: inline-block;
                    width: 24px;
                    height: 24px;
                    line-height: 24px;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .attendance-icon:hover {
                    transform: scale(1.1);
                    opacity: 0.8;
                }
                .attendance-icon.weekend {
                    color: #ccc;
                    font-weight: normal;
                    cursor: default;
                }
                .attendance-icon.upcoming {
                    color: #ccc;
                    font-weight: normal;
                    cursor: default;
                }
                .attendance-icon.absent {
                    color: #ff4d4f;
                    font-weight: bold;
                }
            `}</style>
        </div>
        <ModalWrapper
            open={isModalOpen}
            onRequestClose={handleModalClose}
            title="Attendance Details"
        >
            <AttendanceDetails 
                onClose={handleModalClose} 
                attendanceData={selectedAttendanceData}
            />
        </ModalWrapper>
     
      </>
    );
};

export default Attendance;